import { GridFilterModel } from '@mui/x-data-grid-pro';
import { currencyFormatter } from 'common-ui-library';
import { Dispatch, SetStateAction } from 'react';

export * from './bulkUpload';
export * from './date';
export * from './dayjs';
export * from './emailValidation';
export * from './formatCurrency';
export * from './universalSearch';

export async function copyToClipboard(text: string | number): Promise<any> {
	const text2Copy: string = typeof text === 'number' ? text.toString() : text;
	const canCopyToClipboard = window.isSecureContext && 'clipboard' in navigator;
	if (!canCopyToClipboard) {
		return false;
	}

	return navigator.clipboard.writeText(text2Copy);
}

export const getInvoicesQueryOptions = ({ facilityId }) => {
	const isSubFlagOn = true;
	const isAccountFlag = true;

	type InvoicesFilters = {
		initializationComplete: boolean;
		inputs: {
			invoice_number?: string | null;
			invoice_date_start?: string | null;
			invoice_date_end?: string | null;
			payor_id?: string | null;
			subscription_name?: string | null;
			payor_company_name?: string | null;
			account_id?: string | null;
			payor_name?: string | null;
		};
	};

	type QueryOptions = {
		filter_expressions?: any;
		order?: any;
		offset?: any;
		limit?: any;
	};

	const invoicesFilter: InvoicesFilters = isSubFlagOn
		? {
				initializationComplete: true,
				inputs: {
					invoice_number: null,
					invoice_date_start: null,
					invoice_date_end: null,
					payor_id: null,
					subscription_name: null,
					payor_company_name: null,
					...(isAccountFlag ? { account_id: null } : {}),
				},
			}
		: {
				initializationComplete: true,
				inputs: {
					invoice_number: null,
					invoice_date_start: null,
					invoice_date_end: null,
					payor_id: null,
					payor_name: null,
					payor_company_name: null,
				},
			};

	const invoicesFilterInputs: any = invoicesFilter.inputs;

	const invoicesQueryOptions: any = {
		sortedBy: null,
		limit: null,
		total: null,
		offset: null,
	};

	const queryOptions: QueryOptions = {};

	const invoiceFilterExpressions: any = [];
	const payorFilterExpressions: any = [];
	const subscriptionFilterExpressions: any = [];
	const filterExpressions: any = [];

	if (invoicesFilterInputs.invoice_number !== null) {
		invoiceFilterExpressions.push({
			op: 'AND',
			filters: [
				{
					field: 'readable_id',
					op: 'SUBSTRING',
					value: invoicesFilterInputs.invoice_number,
				},
			],
		});
	}

	if (invoicesFilterInputs.invoice_date_start !== null) {
		const parsedInvoiceDateStart = new Date(invoicesFilterInputs.invoice_date_start);
		const utcParsedInvoiceDateStart = new Date(
			Date.UTC(
				parsedInvoiceDateStart.getFullYear(),
				parsedInvoiceDateStart.getMonth(),
				parsedInvoiceDateStart.getDate(),
			),
		);
		const utcParsedInvoiceDateStartString = utcParsedInvoiceDateStart.toUTCString();

		invoiceFilterExpressions.push({
			op: 'AND',
			filters: [
				{
					field: 'created_at',
					op: 'GTE',
					value: utcParsedInvoiceDateStartString,
				},
			],
		});
	}

	if (invoicesFilterInputs.invoice_date_end !== null) {
		const parsedInvoiceDateStart = new Date(invoicesFilterInputs.invoice_date_end);
		const utcParsedInvoiceDateStart = new Date(
			Date.UTC(
				parsedInvoiceDateStart.getFullYear(),
				parsedInvoiceDateStart.getMonth(),
				parsedInvoiceDateStart.getDate(),
				23,

				59,

				59,
			),
		);
		const utcParsedInvoiceDateStartString = utcParsedInvoiceDateStart.toUTCString();

		invoiceFilterExpressions.push({
			op: 'AND',
			filters: [
				{
					field: 'created_at',
					op: 'LTE',
					value: utcParsedInvoiceDateStartString,
				},
			],
		});
	}

	if (invoicesFilterInputs.payor_id !== null) {
		payorFilterExpressions.push({
			op: 'AND',
			filters: [
				{
					field: 'readable_id',
					op: 'EQ',
					value: invoicesFilterInputs.payor_id,
				},
			],
		});
	}

	if (invoicesFilterInputs.payor_company_name !== null) {
		payorFilterExpressions.push({
			op: 'AND',
			filters: [
				{
					field: 'info_name',
					op: 'SUBSTRING',
					value: invoicesFilterInputs.payor_company_name,
				},
			],
		});
	}

	if (isSubFlagOn) {
		if (invoicesFilterInputs.subscription_name !== null) {
			subscriptionFilterExpressions.push({
				op: 'AND',
				filters: [
					{
						field: 'subscription_name',
						op: 'EQ',
						value: invoicesFilterInputs?.subscription_name?.trim(),
					},
				],
			});
		}
	}

	if (isAccountFlag) {
		if (invoicesFilterInputs.account_id !== null) {
			subscriptionFilterExpressions.push({
				op: 'AND',
				filters: [
					{
						field: 'account_id',
						op: 'SUBSTRING',
						value: invoicesFilterInputs?.account_id?.trim().replace(/\D/g, ''),
					},
				],
			});
		}
	}

	if (!isSubFlagOn) {
		if (invoicesFilterInputs.payor_name !== null) {
			const payorNameInputParts = invoicesFilterInputs?.payor_name?.split(' ') ?? [];

			const payorNameFilters: any = [];
			for (const payorNameInputPart of payorNameInputParts) {
				payorNameFilters.push({
					field: 'billing_firstname',
					op: 'SUBSTRING',
					value: payorNameInputPart,
				});
				payorNameFilters.push({
					field: 'billing_lastname',
					op: 'SUBSTRING',
					value: payorNameInputPart,
				});
			}

			payorFilterExpressions.push({
				op: 'OR',
				filters: payorNameFilters,
			});
		}
	}

	if (invoiceFilterExpressions.length) {
		filterExpressions.push({
			model: 'INVOICE',
			op: 'AND',
			sub_expressions: invoiceFilterExpressions,
		});
	}

	if (payorFilterExpressions.length) {
		filterExpressions.push({
			model: 'PAYOR',
			op: 'AND',
			sub_expressions: payorFilterExpressions,
		});
	}

	if (subscriptionFilterExpressions.length) {
		filterExpressions.push({
			model: 'SUBSCRIPTION',
			op: 'AND',
			sub_expressions: subscriptionFilterExpressions,
		});
	}

	if (facilityId !== null) {
		filterExpressions.push({
			model: 'BILLING_PROFILE',
			op: 'AND',
			sub_expressions: {
				op: 'AND',
				filters: [
					{
						field: 'facility_id',
						op: 'EQ',
						value: facilityId,
					},
				],
			},
		});
	}

	if (filterExpressions.length) {
		queryOptions.filter_expressions = filterExpressions;
	}

	if (invoicesQueryOptions.limit !== null) {
		queryOptions.limit = invoicesQueryOptions.limit;
	}

	if (invoicesQueryOptions.offset !== null) {
		queryOptions.offset = invoicesQueryOptions.offset;
	}

	if (invoicesQueryOptions.sortedBy !== null) {
		let model;
		let field;
		switch (invoicesQueryOptions.sortedBy.columnId) {
			case 'invoice_number':
				model = 'INVOICE';
				field = 'readable_id';
				break;
			case 'invoice_date':
				model = 'INVOICE';
				field = 'created_at';
				break;
			case 'account_id':
				model = 'SUBSCRIPTION';
				field = 'account_id';
				break;
			case 'payor_id':
				model = 'PAYOR';
				field = 'readable_id';
				break;
			case 'subscription_name':
				model = 'SUBSCRIPTION';
				field = 'subscription_name';
				break;
			case 'payor_billing_name':
				model = 'PAYOR';
				field = 'billing_lastname';
				break;
			case 'invoice_amount':
				model = 'INVOICE';
				field = 'amount';
				break;
			case 'invoice_balance':
				model = 'INVOICE';
				field = 'balance';
				break;
			default:
		}
		queryOptions.order = {
			model,
			field,
			direction: invoicesQueryOptions.sortedBy.direction,
		};
	}
	return queryOptions;
};

export const downloadCSVFile = (csv_data, file_name) => {
	// Create CSV file object and feed our
	// csv_data into it
	const CSVFile = new Blob([csv_data], { type: 'text/csv' });

	// Create to temporary link to initiate
	// download process
	const tempLink = document.createElement('a');

	// Download csv file
	tempLink.download = `${file_name}.csv`;
	const url = window.URL.createObjectURL(CSVFile);
	tempLink.href = url;

	// This link should not be displayed
	tempLink.style.display = 'none';
	document.body.appendChild(tempLink);

	// Automatically click the link to trigger download
	tempLink.click();
	document.body.removeChild(tempLink);
};

export const getInvoiceQueryFields = ({ subscriptionNameFlag, accountIdFlag }) =>
	subscriptionNameFlag
		? [
				'total',
				'limit',
				'offset',
				{
					invoices: [
						'id',
						'readable_id',
						'amount',
						'balance',
						'created_at',
						{
							payor: ['id', 'readable_id', 'billing_firstname', 'billing_lastname', 'info_name'],
						},
						{
							subscription: [
								'subscription_name',
								accountIdFlag ? 'account_id' : '',
								{
									billing_profile: ['facility_id'],
								},
							],
						},
					],
				},
			]
		: [
				'total',
				'limit',
				'offset',
				{
					invoices: [
						'id',
						'readable_id',
						'amount',
						'balance',
						'created_at',

						{
							payor: ['id', 'readable_id', 'billing_firstname', 'billing_lastname', 'info_name'],
						},
					],
				},
			];

function getFieldString(field: { [x: string]: any[] }) {
	const type = typeof field;

	if (type === 'string') {
		return `${field}\n`;
	}
	if (type === 'object') {
		const fieldName = Object.keys(field)[0];

		let fieldListString = `${fieldName} {\n`;

		for (let i = 0; i < field[fieldName].length; i++) {
			fieldListString += getFieldString(field[fieldName][i]);
		}
		fieldListString += '}\n';

		return fieldListString;
	}

	return '';
}

export function getFieldListString(fields: any[]) {
	let fieldListString = '';

	if (fields?.length > 1) {
		fields?.forEach((field: any) => (fieldListString += getFieldString(field)));
	}

	return fieldListString;
}

export const enumToTitleCase = (dbEnum, joinSeparator = ' ') => {
	if (dbEnum) {
		const words = dbEnum.split('_');

		const titleCasedWords = words.map((word) => {
			const firstLetter = word.charAt(0);
			const remainingLetters = word.slice(1).toLowerCase();

			return `${firstLetter}${remainingLetters}`;
		});

		return titleCasedWords.join(joinSeparator);
	}

	return '';
};

export const formatBigIntCurrency = ({ value }) =>
	value === 0 || value === '-' ? ' - ' : currencyFormatter.format(+(value / 100).toFixed(2));

export function formatPhoneNumber(number: string) {
	if (!number) return '';
	const returnValues = [];

	if (number.length > 0) returnValues.push('(');
	returnValues.push(number.substring(0, 3));
	if (number.length >= 3) returnValues.push(') ');
	returnValues.push(number.substring(3, 6));
	if (number.length >= 6) returnValues.push('-');
	returnValues.push(number.substring(6, 10));

	return returnValues.join('');
}

// Converts MUI GridFilterModel values to the GraphQL values expected by the monthly API
export function convertMuiFilterForApi(operator: string, val: string) {
	const operatorMap = {
		// Numeric operators
		'=': 'EQ',
		'!=': 'NE',
		'>': 'GT',
		'>=': 'GTE',
		'<': 'LT',
		'<=': 'LTE',

		// String operators
		equals: 'EQ',
		contains: 'LIKE',
		startsWith: 'LIKE',
		endsWith: 'LIKE',

		// Date operators
		is: 'EQ',
		not: 'NE',
		after: 'GT',
		onOrAfter: 'GTE',
		before: 'LT',
		onOrBefore: 'LTE',

		// Multi-type operators
		isEmpty: 'EQ',
		isNotEmpty: 'NE',
		isAnyOf: 'IN',
	};

	const op = operatorMap[operator];
	if (!op) throw new Error(`Invalid operator ${operator}`);

	let value = val;
	switch (operator) {
		case 'contains':
			value = `%${val}%`;
			break;
		case 'startsWith':
			value = `${val}%`;
			break;
		case 'endsWith':
			value = `%${val}`;
			break;
		case 'isEmpty':
		case 'isNotEmpty':
			value = null;
			break;
		default:
	}

	return { op, value };
}

export function convertMuiFilterForV2Api(operator: string): string {
	const operatorMap = {
		// Numeric operators
		'=': 'equals',
		'!=': 'not',
		'>': 'gt',
		'>=': 'gte',
		'<': 'lt',
		'<=': 'lte',

		// Date operators
		is: 'equals',
		after: 'gt',
		onOrAfter: 'gte',
		before: 'lt',
		onOrBefore: 'lte',

		// Multi-type operators
		isEmpty: 'equals',
		isNotEmpty: 'not',
		isAnyOf: 'in',
	};

	const op = operatorMap[operator];
	if (!op) return operator;
	return op;
}

export const genericOnFilterModelChange =
	({
		setFilters,
	}: {
		setFilters: Dispatch<
			SetStateAction<
				{
					field: string;
					op: string;
					value: string | number;
				}[]
			>
		>;
	}) =>
	(filterModel: GridFilterModel) => {
		const { items: filterItems } = filterModel;

		if (!filterItems.length) {
			setFilters([]);
			return;
		}

		const queryFilters = filterItems
			.map((filter) => {
				const { field, operator, value } = filter;

				if (!value && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];

				const op = convertMuiFilterForV2Api(operator);

				return {
					propertyName: field,
					op,
					value: value ?? null,
				};
			})
			.flat();

		if (queryFilters.length) setFilters(queryFilters);
	};

export const dayOfTheMonthFieldOptions = {
	1: '1st',
	2: '2nd',
	3: '3rd',
	4: '4th',
	5: '5th',
	6: '6th',
	7: '7th',
	8: '8th',
	9: '9th',
	10: '10th',
	11: '11th',
	12: '12th',
	13: '13th',
	14: '14th',
	15: '15th',
	16: '16th',
	17: '17th',
	18: '18th',
	19: '19th',
	20: '20th',
	21: '21st',
	22: '22nd',
	23: '23rd',
	24: '24th',
	25: '25th',
	26: '26th',
	27: '27th',
	28: '28th',
};

export const stateFieldOptions = [
	{ value: 'Alabama', label: 'AL' },
	{ value: 'Alaska', label: 'AK' },
	{ value: 'Arizona', label: 'AZ' },
	{ value: 'Arkansas', label: 'AR' },
	{ value: 'California', label: 'CA' },
	{ value: 'Colorado', label: 'CO' },
	{ value: 'Connecticut', label: 'CT' },
	{ value: 'Delaware', label: 'DE' },
	{ value: 'District of Columbia', label: 'DC' },
	{ value: 'Florida', label: 'FL' },
	{ value: 'Georgia', label: 'GA' },
	{ value: 'Hawaii', label: 'HI' },
	{ value: 'Idaho', label: 'ID' },
	{ value: 'Illinois', label: 'IL' },
	{ value: 'Indiana', label: 'IN' },
	{ value: 'Iowa', label: 'IA' },
	{ value: 'Kansas', label: 'KS' },
	{ value: 'Kentucky', label: 'KY' },
	{ value: 'Louisiana', label: 'LA' },
	{ value: 'Maine', label: 'ME' },
	{ value: 'Maryland', label: 'MD' },
	{ value: 'Massachusetts', label: 'MA' },
	{ value: 'Michigan', label: 'MI' },
	{ value: 'Minnesota', label: 'MN' },
	{ value: 'Mississippi', label: 'MS' },
	{ value: 'Missouri', label: 'MO' },
	{ value: 'Montana', label: 'MT' },
	{ value: 'Nebraska', label: 'NE' },
	{ value: 'Nevada', label: 'NV' },
	{ value: 'New Hampshire', label: 'NH' },
	{ value: 'New Jersey', label: 'NJ' },
	{ value: 'New Mexico', label: 'NM' },
	{ value: 'New York', label: 'NY' },
	{ value: 'North Carolina', label: 'NC' },
	{ value: 'North Dakota', label: 'ND' },
	{ value: 'Ohio', label: 'OH' },
	{ value: 'Oklahoma', label: 'OK' },
	{ value: 'Oregon', label: 'OR' },
	{ value: 'Pennsylvania', label: 'PA' },
	{ value: 'Rhode Island', label: 'RI' },
	{ value: 'South Carolina', label: 'SC' },
	{ value: 'South Dakota', label: 'SD' },
	{ value: 'Tennessee', label: 'TN' },
	{ value: 'Texas', label: 'TX' },
	{ value: 'Utah', label: 'UT' },
	{ value: 'Vermont', label: 'VT' },
	{ value: 'Virginia', label: 'VA' },
	{ value: 'Washington', label: 'WA' },
	{ value: 'West Virginia', label: 'WV' },
	{ value: 'Wisconsin', label: 'WI' },
	{ value: 'Wyoming', label: 'WY' },
];

export const provinceFieldOptions = [
	{ value: 'Alberta', label: 'AB' },
	{ value: 'British Columbia', label: 'BC' },
	{ value: 'Manitoba', label: 'MB' },
	{ value: 'New Brunswick', label: 'NB' },
	{ value: 'Newfoundland and Labrador', label: 'NL' },
	{ value: 'Northwest Territories', label: 'NT' },
	{ value: 'Nova Scotia', label: 'NS' },
	{ value: 'Nunavut', label: 'NU' },
	{ value: 'Ontario', label: 'ON' },
	{ value: 'Prince Edward Island', label: 'PE' },
	{ value: 'Quebec', label: 'QC' },
	{ value: 'Saskatchewan', label: 'SK' },
	{ value: 'Yukon', label: 'YT' },
];

export function capitalize(str) {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export const fileToBase64 = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const { result } = reader;
			const base64 = typeof result === 'string' ? result.split(',')[1] : '';
			resolve(base64);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
